import { useQuery } from '@tanstack/react-query';
import { API_URL } from './constants';
import { authHeaders } from './helpers';
import type { Schedule } from './types';

export function useSchedules() {
  const query = useQuery<Schedule[], Error>({
    queryKey: ['pay-schedules'],
    queryFn: async () => {
      const res = await fetch(API_URL + '/api/v1/payroll/schedules', { headers: authHeaders() });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      return Array.isArray(data) ? (data as Schedule[]) : [];
    },
    refetchOnWindowFocus: false,
  });

  const refetch = async (): Promise<void> => {
    await query.refetch();
  };

  const deleteSchedule = async (id: string | number): Promise<void> => {
    const res = await fetch(API_URL + '/api/v1/payroll/schedules/' + id, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({} as any));
      throw new Error(errData.detail || 'Could not delete schedule');
    }
    await refetch();
  };

  const duplicateSchedule = async (id: string | number): Promise<void> => {
    const res = await fetch(API_URL + '/api/v1/payroll/schedules/' + id + '/duplicate', {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    await refetch();
  };

  const togglePause = async (schedule: Schedule): Promise<void> => {
    const endpoint = schedule.is_paused ? 'resume' : 'pause';
    const res = await fetch(
      API_URL + '/api/v1/payroll/schedules/' + schedule.id + '/' + endpoint,
      { method: 'POST', headers: authHeaders() },
    );
    if (!res.ok) throw new Error('HTTP ' + res.status);
    await refetch();
  };

  const setDefault = async (id: string | number): Promise<void> => {
    const res = await fetch(API_URL + '/api/v1/payroll/schedules/' + id, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ is_default: true }),
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    await refetch();
  };

  return {
    schedules: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch,
    deleteSchedule,
    duplicateSchedule,
    togglePause,
    setDefault,
  };
}